/* eslint-disable react/prop-types */
import { forwardRef, useMemo } from 'react';

import { ReactComponent as BookOpenIcon } from 'assets/icons/book-open.svg';
import { ReactComponent as ClockIcon } from 'assets/icons/clock.svg';
import { secondsToText } from 'libs/time';
import Button from 'components/Button';
import Card from 'components/Card';
import Progress from 'components/Progress';
import {
  EXAM_STATUS_ACTIVE,
  EXAM_STATUS_DOING,
  EXAM_STATUS_DONE,
  EXAM_STATUS_NOT_ACTIVE,
  EXAM_STATUS_WRONG_DATE,
} from 'constant';
import Typography from 'components/Typography';

function StatusText({ status }) {
  switch (status) {
    case EXAM_STATUS_NOT_ACTIVE:
      return (
        <Typography
          as="span"
          variant="small"
          className="font-normal px-8 py-2 bg-gray-200 text-gray-800 rounded-full"
        >
          Tidak Aktif
        </Typography>
      );
    case EXAM_STATUS_WRONG_DATE:
      return (
        <Typography
          as="span"
          variant="small"
          className="font-normal px-8 py-2 bg-gray-200 text-gray-800 rounded-full"
        >
          Tanggal Tidak Sesuai
        </Typography>
      );
    case EXAM_STATUS_ACTIVE:
      return (
        <Typography
          as="span"
          variant="small"
          className="font-normal px-8 py-2 bg-green-100 text-primary rounded-full"
        >
          Aktif
        </Typography>
      );
    case EXAM_STATUS_DONE:
      return (
        <Typography
          as="span"
          variant="small"
          className="font-normal px-8 py-2 bg-red-200 text-yellow-700 rounded-full"
        >
          Selesai
        </Typography>
      );
    case EXAM_STATUS_DOING:
      return (
        <Typography
          as="span"
          variant="small"
          className="font-normal px-8 py-2 bg-yellow-200 text-yellow-700 rounded-full"
        >
          Sedang Dikerjakan
        </Typography>
      );

    default:
      return null;
  }
}

const ExamCard = forwardRef(
  (
    {
      examNumber,
      examTotal,
      name,
      questionTotal,
      duration,
      remainingTime,
      status,
      percentage,
      onStartClick,
      disableStartButton,
    },
    ref
  ) => {
    const durationText = useMemo(() => secondsToText(duration), [duration]);
    const remainingTimeText = useMemo(
      () => secondsToText(remainingTime),
      [remainingTime]
    );

    return (
      <Card ref={ref} className="mt-8">
        <div>
          <Typography variant="small" color="muted" className="font-normal">
            Ujian Ke-{examNumber} dari {examTotal}
          </Typography>

          <div className="mt-1 flex flex-col gap-3 md:flex-row md:items-center md:justify-start md:gap-6">
            <Typography variant="subtitle2" className="font-normal">
              {name}
            </Typography>
            <StatusText status={status} />
          </div>
        </div>

        <div className="mt-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-0">
            <div className="md:col-span-10">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <Typography variant="subtitle3" color="muted">
                    Soal
                  </Typography>
                  <Typography
                    variant="subtitle3"
                    className="flex items-center gap-2 font-normal mt-2"
                  >
                    <BookOpenIcon className="text-gray-600" />
                    {questionTotal} Soal
                  </Typography>
                </div>
                <div>
                  <Typography variant="subtitle3" color="muted">
                    Durasi
                  </Typography>
                  <Typography
                    variant="subtitle3"
                    className="flex items-center gap-2 font-normal mt-2"
                  >
                    <ClockIcon className="text-gray-600" />
                    {durationText}
                  </Typography>
                </div>
                <div>
                  <Typography variant="subtitle3" color="muted">
                    Sisa Waktu
                  </Typography>
                  <Typography
                    variant="subtitle3"
                    className="flex items-center gap-2 font-normal mt-2 text-danger"
                  >
                    <ClockIcon className="text-red-400" />
                    {remainingTimeText === '' ? 'Habis' : remainingTimeText}
                  </Typography>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-3 xl:w-11/12">
                <Progress percentage={percentage} className="flex-grow m-0" />
                <Typography as="span" className="flex-none text-primary">
                  {percentage}%
                </Typography>
              </div>
            </div>

            <div className="md:col-span-2 md:flex md:items-end md:justify-start">
              <Button
                color="primary"
                onClick={onStartClick}
                disabled={disableStartButton}
                className="w-full md:w-auto md:px-8"
              >
                Mulai
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }
);

ExamCard.displayName = 'ExamCard';

export default ExamCard;
